/**
 * LinkedInArticle — /#/library/linkedin/:slug
 *
 * Renders a single Bryan K. Short LinkedIn Pulse article inside the Reference Library.
 * The original LinkedIn URL is preserved and rendered at the foot of every article.
 *
 * Imported 2026-06-11.
 *
 * Canon guardrails (CANON_VISUALS_LOCKED.md 2026-05-29):
 *   5-color palette only. No exclamation points. No emojis.
 *   M-PACT hyphenated. Bryan K. Short, Founder and CEO, Mi6 Group.
 */

import { useParams, useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import MarkdownRenderer from "@/components/content/MarkdownRenderer";
import {
  LINKEDIN_ARTICLES,
  LINKEDIN_SECTION_LABELS,
} from "@/content/index";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const GRAY_LINE = "#E5E5E7";
const GRAY_MUTED = "#6B6B70";

export default function LinkedInArticle() {
  const params = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const slug = params.slug ?? "";

  const article = LINKEDIN_ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <OperatorShell
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Reference Library", href: "/library" },
          { label: "Not found" },
        ]}
        pageTitle="Not found"
      >
        <p style={{ color: GRAY_MUTED, fontSize: 14 }}>
          Article not found. Return to{" "}
          <button
            type="button"
            onClick={() => setLocation("/library")}
            style={{
              color: RED,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Reference Library
          </button>
          .
        </p>
      </OperatorShell>
    );
  }

  const sectionLabel = LINKEDIN_SECTION_LABELS[article.section];

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Reference Library", href: "/library" },
        { label: article.title },
      ]}
      pageTitle={article.title}
    >
      {/* Back link */}
      <button
        type="button"
        onClick={() => setLocation("/library")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: GRAY_MUTED,
          fontSize: 12,
          fontWeight: 600,
          padding: 0,
          marginBottom: 32,
          letterSpacing: "0.04em",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = BLACK; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = GRAY_MUTED; }}
      >
        ← Reference Library
      </button>

      <article
        style={{
          background: "#FFFFFF",
          border: `1px solid ${GRAY_LINE}`,
          borderRadius: 10,
          padding: "36px 40px",
          maxWidth: 760,
        }}
      >
        {/* Eyebrow */}
        <div style={{ marginBottom: 14 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: RED,
            }}
          >
            LinkedIn Article
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 28,
            fontWeight: 700,
            color: BLACK,
            margin: "0 0 10px",
            lineHeight: 1.2,
          }}
        >
          {article.title}
        </h1>

        {/* Byline + date */}
        <div
          style={{
            fontSize: 13,
            color: GRAY_MUTED,
            marginBottom: 28,
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
          }}
        >
          <span style={{ fontWeight: 600, color: "#525258" }}>
            Bryan K. Short
          </span>
          <span aria-hidden="true">·</span>
          <span>{article.dateDisplay}</span>
          <span aria-hidden="true">·</span>
          <span>{sectionLabel}</span>
        </div>

        {/* Body */}
        <MarkdownRenderer markdown={article.body} maxWidth={680} fontSize={14.5} />

        {/* Originally published on LinkedIn */}
        <div
          style={{
            marginTop: 40,
            paddingTop: 24,
            borderTop: `1px solid ${GRAY_LINE}`,
            fontSize: 13,
            lineHeight: 1.65,
            color: "#525258",
          }}
        >
          Originally published on LinkedIn ·{" "}
          <a
            href={article.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: RED,
              textDecoration: "none",
              fontWeight: 600,
              borderBottom: `1px solid ${RED}`,
              paddingBottom: 1,
            }}
          >
            View on LinkedIn
          </a>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: 20,
            fontSize: 11,
            color: "#9CA3AF",
            lineHeight: 1.6,
          }}
        >
          Mi6 Group · Attilius · Reference Library · June 2026
        </div>
      </article>
    </OperatorShell>
  );
}
